/**
 * RemitDetail — rapport d'une période de remise DAS (Batch #14.283) : détail par employé, corrections (calculé → corrigé
 * avec motif), PDF / Excel avec l'en-tête du cabinet, Drive, courriel protégé (adresse + mot de passe fixes), publication au portail.
 */
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { FileDown, FileSpreadsheet, Globe, Mail, Pencil, Save, X, Undo2 } from "lucide-react";
import { toast } from "@/lib/toast";
import DriveSendButton from "@/components/admin/DriveSendButton";
import { API, authH, fmtCAD, Btn, inputCls, thCls, tdCls, downloadFile, err } from "./_shared";

const fmtSigned = (v) => (v > 0 ? "+" : "") + fmtCAD(v);
const shortLabel = (l) => l.split(" — ")[0].split(" (")[0].replace(" retenu", "").replace("Impôt du Québec", "Impôt QC").replace("Assurance-emploi", "AE") + (l.includes("salarié") ? " sal." : l.includes("employeur") ? " empl." : "");

function SummaryTable({ d, editing, lines, setLines, testPrefix }) {
  const adj = (code) => Number(lines[code]?.amount) || 0;
  const declared = (code) => (editing ? (d.computed[code] || 0) + adj(code) : (d.declared[code] || 0));
  const showAdj = editing || d.has_adjustments;
  const ytd = d.ytd?.computed || d.ytd_computed || null;
  const totAdj = d.fields.reduce((a, f) => a + (editing ? adj(f.code) : (d.adjustments[f.code]?.amount || 0)), 0);
  return (
    <table className="w-full text-sm" data-testid={`${testPrefix}-summary`}>
      <thead className="bg-slate-50 border-y border-slate-200"><tr>
        <th className={thCls}>Élément</th>
        {showAdj && <th className={`${thCls} text-right`}>Calculé</th>}
        {showAdj && <th className={`${thCls} text-right`}>Correction</th>}
        <th className={`${thCls} text-right`}>{showAdj ? "À remettre" : ytd ? "Période" : "Montant"}</th>
        {ytd && <th className={`${thCls} text-right`} title={d.ytd ? `Bulletins approuvés du ${d.ytd.from} au ${d.ytd.to}` : d.ytd_label}>Cumulatif annuel</th>}
      </tr></thead>
      <tbody className="divide-y divide-slate-100">
        {d.fields.map((f) => (
          <tr key={f.code} data-testid={`${testPrefix}-line-${f.code}`}>
            <td className={tdCls}>
              {f.label}
              {!editing && d.adjustments[f.code]?.note && <div className="text-[10px] text-amber-700 italic">{d.adjustments[f.code].note}</div>}
            </td>
            {showAdj && <td className={`${tdCls} text-right font-mono text-xs text-slate-500`}>{fmtCAD(d.computed[f.code] || 0)}</td>}
            {showAdj && (
              <td className={`${tdCls} text-right`}>
                {editing ? (
                  <div className="flex flex-col items-end gap-1">
                    <input type="number" step="0.01" className={`${inputCls} w-32 text-right py-1`} value={lines[f.code]?.amount ?? ""} placeholder="0,00"
                      onChange={(e) => setLines((l) => ({ ...l, [f.code]: { ...(l[f.code] || {}), amount: e.target.value } }))} data-testid={`${testPrefix}-adj-${f.code}`} />
                    <input className={`${inputCls} w-56 py-1 text-xs`} value={lines[f.code]?.note ?? ""} placeholder="Motif"
                      onChange={(e) => setLines((l) => ({ ...l, [f.code]: { ...(l[f.code] || {}), note: e.target.value } }))} data-testid={`${testPrefix}-note-${f.code}`} />
                  </div>
                ) : (d.adjustments[f.code] ? <span className="font-mono text-xs font-semibold text-amber-700">{fmtSigned(d.adjustments[f.code].amount)}</span> : <span className="text-slate-300">—</span>)}
              </td>
            )}
            <td className={`${tdCls} text-right font-mono font-semibold`} data-testid={`${testPrefix}-declared-${f.code}`}>{fmtCAD(declared(f.code))}</td>
            {ytd && <td className={`${tdCls} text-right font-mono text-slate-500`} data-testid={`${testPrefix}-ytd-${f.code}`}>{fmtCAD(ytd[f.code] || 0)}</td>}
          </tr>
        ))}
      </tbody>
      <tfoot><tr className="bg-slate-50 font-bold border-t-2 border-slate-300">
        <td className={tdCls}>TOTAL À REMETTRE</td>
        {showAdj && <td className={`${tdCls} text-right font-mono text-slate-500`}>{fmtCAD(d.computed_total)}</td>}
        {showAdj && <td className={`${tdCls} text-right font-mono text-amber-700`}>{totAdj ? fmtSigned(totAdj) : "—"}</td>}
        <td className={`${tdCls} text-right font-mono`} data-testid={`${testPrefix}-total`}>{fmtCAD(d.fields.reduce((a, f) => a + declared(f.code), 0))}</td>
        {ytd && <td className={`${tdCls} text-right font-mono text-slate-600`} data-testid={`${testPrefix}-ytd-total`}>{fmtCAD(d.fields.reduce((a, f) => a + (ytd[f.code] || 0), 0))}</td>}
      </tr></tfoot>
    </table>
  );
}

function EmployeeRows({ d, testPrefix }) {
  if (!d.rows.length) return <p className="text-xs text-slate-500">Aucun bulletin approuvé dans cette période.</p>;
  return (
    <div className="overflow-x-auto -mx-4">
      <table className="w-full text-xs" data-testid={`${testPrefix}-rows`}>
        <thead className="bg-slate-50 border-y border-slate-200"><tr>
          <th className={thCls}>Employé</th><th className={thCls}>Paie du</th>
          {d.fields.map((f) => <th key={f.code} className={`${thCls} text-right`}>{shortLabel(f.label)}</th>)}
          <th className={`${thCls} text-right`}>Total</th>
        </tr></thead>
        <tbody className="divide-y divide-slate-100">
          {d.rows.map((r, i) => (
            <tr key={i} data-testid={`${testPrefix}-row-${i}`}>
              <td className={tdCls}>{r.employee}{r.employee_no && <span className="text-slate-400"> · {r.employee_no}</span>}</td>
              <td className={tdCls}>{r.pay_date}</td>
              {d.fields.map((f) => <td key={f.code} className={`${tdCls} text-right font-mono`}>{fmtCAD(r[f.code])}</td>)}
              <td className={`${tdCls} text-right font-mono font-semibold`}>{fmtCAD(r.total)}</td>
            </tr>
          ))}
        </tbody>
        <tfoot><tr className="bg-slate-50 font-bold">
          <td className={tdCls} colSpan={2}>Total calculé ({d.nb_stubs} bulletin{d.nb_stubs > 1 ? "s" : ""})</td>
          {d.fields.map((f) => <td key={f.code} className={`${tdCls} text-right font-mono`}>{fmtCAD(d.computed[f.code])}</td>)}
          <td className={`${tdCls} text-right font-mono`}>{fmtCAD(d.computed_total)}</td>
        </tr></tfoot>
      </table>
    </div>
  );
}

function YtdRows({ d, testPrefix }) {
  const y = d.ytd;
  if (!y?.rows?.length) return <p className="text-xs text-slate-500">Aucun bulletin approuvé depuis le 1er janvier.</p>;
  return (
    <div className="overflow-x-auto -mx-4">
      <table className="w-full text-xs" data-testid={`${testPrefix}-ytd-rows`}>
        <thead className="bg-slate-50 border-y border-slate-200"><tr>
          <th className={thCls}>Employé</th><th className={`${thCls} text-right`}>Paies</th>
          {d.fields.map((f) => <th key={f.code} className={`${thCls} text-right`}>{shortLabel(f.label)}</th>)}
          <th className={`${thCls} text-right`}>Total</th>
        </tr></thead>
        <tbody className="divide-y divide-slate-100">
          {y.rows.map((r) => (
            <tr key={r.employee_id} data-testid={`${testPrefix}-ytd-row-${r.employee_id}`}>
              <td className={tdCls}>{r.employee}{r.employee_no && <span className="text-slate-400"> · {r.employee_no}</span>}</td>
              <td className={`${tdCls} text-right font-mono`}>{r.nb_stubs}</td>
              {d.fields.map((f) => <td key={f.code} className={`${tdCls} text-right font-mono`}>{fmtCAD(r[f.code])}</td>)}
              <td className={`${tdCls} text-right font-mono font-semibold`}>{fmtCAD(r.total)}</td>
            </tr>
          ))}
        </tbody>
        <tfoot><tr className="bg-slate-50 font-bold">
          <td className={tdCls}>Total cumulatif</td><td className={`${tdCls} text-right font-mono`}>{y.nb_stubs}</td>
          {d.fields.map((f) => <td key={f.code} className={`${tdCls} text-right font-mono`}>{fmtCAD(y.computed[f.code])}</td>)}
          <td className={`${tdCls} text-right font-mono`}>{fmtCAD(y.total)}</td>
        </tr></tfoot>
      </table>
    </div>
  );
}

export default function RemitDetail({ masterId, authority, periodKey, onClose, onChanged }) {
  const [d, setD] = useState(null);
  const [editing, setEditing] = useState(false);
  const [lines, setLines] = useState({});
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState("");
  const base = `/admin/payroll/${masterId}/remittances/${authority}/${periodKey}`;
  const tp = `paie-remit-detail`;

  const load = useCallback(async () => {
    try { const r = await axios.get(`${API}${base}`, { headers: authH() }); setD(r.data); } catch (e) { err(e, "Période introuvable"); onClose(); }
  }, [base, onClose]);
  useEffect(() => { load(); }, [load]);
  if (!d) return <div className="bg-white border border-slate-200 rounded-xl p-4 text-sm text-slate-500">Chargement du rapport…</div>;

  const startEdit = () => { setLines(Object.fromEntries(Object.entries(d.adjustments).map(([c, a]) => [c, { amount: a.amount, note: a.note || "" }]))); setNote(d.adjust_note || ""); setEditing(true); };
  const save = async () => {
    setBusy("save");
    try {
      const r = await axios.put(`${API}${base}/adjustments`, { lines, note }, { headers: authH() });
      setD(r.data); setEditing(false); toast.success("Montants corrigés"); onChanged?.();
    } catch (e) { err(e); } finally { setBusy(""); }
  };
  const reset = async () => {
    if (!window.confirm("Retirer toutes les corrections et revenir aux montants calculés ?")) return;
    setBusy("save");
    try { const r = await axios.put(`${API}${base}/adjustments`, { lines: {}, note: "" }, { headers: authH() }); setD(r.data); setEditing(false); toast.success("Corrections retirées"); onChanged?.(); } catch (e) { err(e); } finally { setBusy(""); }
  };
  const email = async (kind = "internal") => {
    if (!d.email_to) return toast.error("Aucune adresse courriel DAS — réglez-la dans Réglages paie");
    const what = kind === "client" ? "le rapport CLIENT (jumelé, identique au portail)" : "le rapport interne (détail salarié / employeur)";
    if (!window.confirm(`Envoyer ${what} ${d.authority_short} — ${d.label} à ${d.email_to} ?${d.password_set ? "" : "\n\nAttention : aucun mot de passe PDF configuré (Réglages paie)."}`)) return;
    setBusy(kind === "client" ? "email-client" : "email");
    try {
      const r = await axios.post(`${API}${base}/${kind === "client" ? "client-email" : "email"}`, {}, { headers: authH() });
      if (r.data.ok) toast.success(`Rapport ${kind === "client" ? "client " : ""}envoyé à ${r.data.to}${r.data.protected ? " (PDF protégé)" : ""}`); else toast.error(`Envoi impossible à ${r.data.to} : ${r.data.info}`);
      load(); onChanged?.();
    } catch (e) { err(e); } finally { setBusy(""); }
  };
  const publish = async () => {
    if (!window.confirm(`Publier ${fmtCAD(d.total)} dans « DAS client » pour ${d.portal_label} ? Le client verra ces montants dans son portail.`)) return;
    setBusy("publish");
    try { await axios.post(`${API}${base}/publish`, { note: "" }, { headers: authH() }); toast.success(`Montants publiés au portail — ${d.portal_label}`); load(); onChanged?.(); } catch (e) { err(e); } finally { setBusy(""); }
  };
  const lastOf = (arr, pred) => { const xs = (arr || []).filter(pred); return xs.length ? xs[xs.length - 1] : null; };
  const lastSentClient = lastOf(d.sent_log, (x) => x.kind === "client");
  const lastSentInternal = lastOf(d.sent_log, (x) => x.kind !== "client");
  const lastDriveClient = lastOf(d.drive_uploads, (x) => x.doc_type === "das_remit_client");
  const lastDriveInternal = lastOf(d.drive_uploads, (x) => x.doc_type !== "das_remit_client");
  const DRIVE_FOLDER_LABEL = { fiscal: "Fiscal", immeuble: "Immeuble", entrepreneur: "Entrepreneur", paie: "Paie" };
  const DriveInfo = ({ u, id }) => u ? <span data-testid={id}>Drive : classé le {String(u.at).slice(0, 10)} dans « {DRIVE_FOLDER_LABEL[u.expertise] || u.expertise} »{u.web_link && <> — <a href={u.web_link} target="_blank" rel="noreferrer" className="underline" data-testid={`${id}-link`}>ouvrir le fichier</a></>}</span> : null;
  const SentInfo = ({ u }) => u ? <span>Dernier envoi : {String(u.at).slice(0, 16).replace("T", " ")} → {u.to} {u.ok ? "✓" : `✗ ${u.info}`}</span> : null;

  return (
    <div className="bg-white border-2 border-rose-200 rounded-xl p-4 space-y-4" data-testid={tp}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h3 className="text-sm font-bold text-slate-800">Rapport de remises — {d.authority_label}</h3>
          <p className="text-xs text-slate-500">{d.label} · du {d.start} au {d.end} · échéance <b>{d.due}</b> · {d.frequency_label} · {d.nb_stubs} bulletin(s) approuvé(s)
            {d.paid_at && <span className="text-emerald-700 font-semibold"> · remise faite le {d.paid_at}{d.confirmation ? ` (${d.confirmation})` : ""}</span>}</p>
          {d.pd7a && <p className="text-xs text-slate-700" data-testid={`${tp}-pd7a`}>PD7A — Rémunération fédérale de la période (T4 case 14) : <b>{fmtCAD(d.pd7a.gross_payroll)}</b>{d.pd7a.qc_only_benefits > 0 && <span className="text-slate-500" data-testid={`${tp}-pd7a-qc-only`}> (sans {fmtCAD(d.pd7a.qc_only_benefits)} d'avantages imposables Québec seulement — assurance CCQ / collective)</span>}{d.pd7a.pay_dates?.length > 0 && <span className="text-slate-500"> ({d.pd7a.pay_dates.length} paie{d.pd7a.pay_dates.length > 1 ? "s" : ""} : {d.pd7a.pay_dates.join(", ")})</span>} · Nombre d'employés payés dans la période : <b data-testid={`${tp}-pd7a-employees`}>{d.pd7a.employee_count}</b>{d.pd7a.pay_dates?.length > 1 && <span className="text-slate-500"> (dont {d.pd7a.employee_count_last} à la dernière paie du {d.pd7a.last_pay_date})</span>}</p>}
          {d.pd7a?.pending_stubs > 0 && <p className="text-[11px] text-amber-700" data-testid={`${tp}-pd7a-pending`}>⚠ {d.pd7a.pending_stubs} bulletin(s) non approuvé(s) dans la période ({d.pd7a.pending_dates.join(", ")}) — {fmtCAD(d.pd7a.pending_gross)} de rémunération fédérale (T4 case 14) non comprise : approuvez la paie pour l'inclure.</p>}
          {d.has_adjustments && <p className="text-[11px] text-amber-700" data-testid={`${tp}-adjusted`}>Montants corrigés le {String(d.adjusted_at || "").slice(0, 10)}{d.adjusted_by ? ` par ${d.adjusted_by}` : ""}{d.adjust_note ? ` — ${d.adjust_note}` : ""}</p>}
        </div>
        <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100" aria-label="Fermer" data-testid={`${tp}-close`}><X className="w-4 h-4" /></button>
      </div>

      <div className="space-y-2" data-testid={`${tp}-actions`}>
        <div className="rounded-xl border border-emerald-200 bg-emerald-50/50 p-2.5" data-testid={`${tp}-client-actions`}>
          <div className="flex flex-wrap gap-2 items-center">
            <span className="text-[11px] font-bold text-emerald-900 mr-1">Rapport client — jumelé (identique au portail)</span>
            <Btn tone="slate" icon={FileDown} onClick={() => downloadFile(`${base}/client-report.pdf`, `DAS_client_${authority}_${periodKey}.pdf`)} testId={`${tp}-client-pdf`}>PDF</Btn>
            <Btn tone="slate" icon={FileSpreadsheet} onClick={() => downloadFile(`${base}/client-report.xlsx`, `DAS_client_${authority}_${periodKey}.xlsx`)} testId={`${tp}-client-xlsx`}>Excel</Btn>
            <Btn tone="slate" icon={Mail} onClick={() => email("client")} busy={busy === "email-client"} testId={`${tp}-client-email`}>Courriel{d.email_to ? "" : " (adresse manquante)"}</Btn>
            <DriveSendButton docType="das_remit_client" docId={`${masterId}:${authority}:${periodKey}`} defaultExpertise="paie" title="Classer le rapport client (jumelé) dans Google Drive" onSent={load} />
            <Btn tone="rose" icon={Globe} onClick={publish} busy={busy === "publish"} testId={`${tp}-publish`}>Publier au portail</Btn>
            <span className="text-[11px] text-slate-500 ml-auto">Une case RRQ, une case RQAP{authority === "federal" ? " / AE" : ""} (parts salarié + employeur) — pour les clients qui ne passent pas par le portail.</span>
          </div>
          {(lastSentClient || lastDriveClient) && <div className="text-[11px] text-slate-500 flex flex-wrap gap-x-4 mt-1.5" data-testid={`${tp}-client-status`}><SentInfo u={lastSentClient} /><DriveInfo u={lastDriveClient} id={`${tp}-client-drive-info`} /></div>}
        </div>
        <div className="flex flex-wrap gap-2 items-center" data-testid={`${tp}-internal-actions`}>
          <span className="text-[11px] font-bold text-slate-700 mr-1">Rapport interne — détail salarié / employeur</span>
          {!editing && <Btn tone="slate" icon={Pencil} onClick={startEdit} testId={`${tp}-edit`}>Corriger les montants</Btn>}
          <Btn tone="slate" icon={FileDown} onClick={() => downloadFile(`${base}/report.pdf`, `Remises_DAS_${authority}_${periodKey}.pdf`)} testId={`${tp}-pdf`}>PDF</Btn>
          <Btn tone="slate" icon={FileSpreadsheet} onClick={() => downloadFile(`${base}/report.xlsx`, `Remises_DAS_${authority}_${periodKey}.xlsx`)} testId={`${tp}-xlsx`}>Excel</Btn>
          <DriveSendButton docType="das_remit" docId={`${masterId}:${authority}:${periodKey}`} defaultExpertise="paie" title="Classer le rapport interne dans Google Drive" onSent={load} />
          <Btn tone="slate" icon={Mail} onClick={() => email("internal")} busy={busy === "email"} testId={`${tp}-email`}>Courriel{d.email_to ? "" : " (adresse manquante)"}</Btn>
          <span className="text-[11px] text-slate-500 ml-auto">
            {d.email_to ? <>Courriel → <b>{d.email_to}</b> · PDF {d.password_set ? "protégé par mot de passe" : <span className="text-amber-700">sans mot de passe</span>}</> : <>Adresse DAS non réglée — <Link to={`/admin/paie/${masterId}/reglages`} className="underline">Réglages paie</Link></>}
          </span>
        </div>
      </div>
      {(lastSentInternal || d.published || lastDriveInternal) && (
        <div className="text-[11px] text-slate-500 flex flex-wrap gap-x-4" data-testid={`${tp}-status`}>
          <SentInfo u={lastSentInternal} />
          <DriveInfo u={lastDriveInternal} id={`${tp}-drive-info`} />
          {d.published && <span>Publié au portail ({d.portal_label}) : <b>{fmtCAD(d.published.total)}</b>{Math.abs(d.published.total - d.total) > 0.004 && <span className="text-amber-700"> — différent du rapport actuel</span>}</span>}
        </div>
      )}

      <div className="overflow-x-auto -mx-4">
        <SummaryTable d={d} editing={editing} lines={lines} setLines={setLines} testPrefix={tp} />
      </div>
      {editing && (
        <div className="space-y-2">
          <input className={inputCls} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note générale sur la correction (facultatif)" data-testid={`${tp}-adjust-note`} />
          <div className="flex flex-wrap gap-2 justify-end">
            {d.has_adjustments && <Btn tone="ghost" icon={Undo2} onClick={reset} busy={busy === "save"} testId={`${tp}-reset`}>Retirer les corrections</Btn>}
            <Btn tone="ghost" onClick={() => setEditing(false)} testId={`${tp}-cancel`}>Annuler</Btn>
            <Btn tone="rose" icon={Save} onClick={save} busy={busy === "save"} testId={`${tp}-save`}>Enregistrer</Btn>
          </div>
        </div>
      )}

      <div>
        <h4 className="text-xs font-bold text-slate-700 mb-2">Détail par employé — période</h4>
        <EmployeeRows d={d} testPrefix={tp} />
      </div>
      {d.ytd && (
        <div>
          <h4 className="text-xs font-bold text-slate-700 mb-2">Cumulatif annuel par employé <span className="font-normal text-slate-500">— bulletins approuvés du {d.ytd.from} au {d.ytd.to}</span></h4>
          <YtdRows d={d} testPrefix={tp} />
        </div>
      )}
    </div>
  );
}
